import { CUSTOM_ELEMENTS_SCHEMA, Component, ElementRef, OnInit, ViewEncapsulation, inject, signal, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { MatButtonModule } from '@angular/material/button';
import { PlaceAutocompleteElementExtended } from './charging-station-location-map-address-picker-modal.interface';

@Component({
  selector: 'adm-charging-station-location-map-address-picker-modal',
  templateUrl: './charging-station-location-map-address-picker-modal.component.html',
  styleUrls: ['./charging-station-location-map-address-picker-modal.component.scss'],
  imports: [GoogleMap, MapMarker, MatButtonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
})
export class ChargingStationLocationMapAddressPickerModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ChargingStationLocationMapAddressPickerModalComponent>>(MatDialogRef);
  data = inject<{
    preselectedLocation: string;
    preselectedLatitude: number;
    preselectedLongitude: number;
  }>(MAT_DIALOG_DATA);

  googleMap!: google.maps.Map;
  options = signal<google.maps.MapOptions | undefined>(undefined);
  searchbox!: google.maps.places.PlaceAutocompleteElement;
  currentMarker = signal<google.maps.marker.AdvancedMarkerElement | undefined>(undefined);
  mapLoadAllowed = signal(false);
  searchElement = viewChild.required<ElementRef>('search');
  lattitude = viewChild<ElementRef>('lattitude');
  longitude = viewChild<ElementRef>('longitude');
  gmpShadowPatched = signal(false);
  async ngOnInit() {
    await Promise.all([google.maps.importLibrary('marker'), google.maps.importLibrary('places'), this.setupShadowDOMStyling()]);

    this.options.set({
      mapTypeId: google.maps.MapTypeId.HYBRID,
      zoomControl: true,
      scrollwheel: true,
      disableDoubleClickZoom: true,
      disableDefaultUI: true,
      tilt: 0,
      mapTypeControl: true,
      mapTypeControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
      fullscreenControl: true,
      mapId: 'map-address-picker',
      center: new google.maps.LatLng(this.data.preselectedLatitude || 55.6867243, this.data.preselectedLongitude || 12.5700724),
    });

    this.mapLoadAllowed.set(true);
  }

  public onMapReady(map: google.maps.Map): void {
    this.googleMap = map;

    // Create the PlaceAutocompleteElement
    const autocompleteElement = this.searchElement().nativeElement as PlaceAutocompleteElementExtended;

    // Set options
    autocompleteElement.includedRegionCodes = ['dk'];

    // Style it to match your input
    autocompleteElement.style.width = '100%';
    autocompleteElement.style.border = '0';
    autocompleteElement.style.fontSize = '18px';

    // Listen for place selection in dropdown
    autocompleteElement.addEventListener('gmp-select', async ({ placePrediction }) => {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location'],
      });

      if (!place.location) return;
      const location = new google.maps.LatLng(place.location.lat(), place.location.lng());

      this.googleMap?.panTo(location);
      this.setMarker(location, true);
    });

    // listens for place select on the map
    autocompleteElement.addEventListener('gmp-placeselect', async ({ place }) => {
      if (!place.location) {
        console.warn('No location in place');
        return;
      }

      const location = new google.maps.LatLng(place.location.lat(), place.location.lng());
      this.googleMap?.panTo(location);
      this.setMarker(location, true);
    });

    if (this.data) {
      if (this.data.preselectedLatitude != null && this.data.preselectedLongitude != null) {
        const location = new google.maps.LatLng(this.data.preselectedLatitude, this.data.preselectedLongitude);
        this.setMarker(location, true);
      } else if (this.data.preselectedLocation != null) {
        this.setupInitialAddress(this.data.preselectedLocation);
      }
    }
  }

  setupInitialAddress(address: string) {
    this.searchElement().nativeElement.value = address;
    const request = {
      query: address,
      fields: ['name', 'geometry'],
    };

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: address }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const location = results[0].geometry.location;
        this.googleMap.panTo(location);
        this.setMarker(location, true);
      }
    });
  }

  approveLocation() {
    this.dialogRef.close({ lat: this.lattitude()?.nativeElement.value, lng: this.longitude()?.nativeElement.value });
  }

  setMarker(location: google.maps.LatLng, setFocus: boolean) {
    const previousMarker = this.currentMarker();
    if (previousMarker) {
      previousMarker.map = null;
    }

    this.currentMarker.set(
      new google.maps.marker.AdvancedMarkerElement({
        map: this.googleMap,
        position: {
          lat: +location.lat(),
          lng: +location.lng(),
        },
        gmpDraggable: true,
      })
    );

    if (setFocus) {
      this.googleMap.panTo(location);
    }
    const latRef = this.lattitude();
    if (latRef) {
      latRef.nativeElement.value = location.lat();
    }
    const lngRef = this.longitude();
    if (lngRef) {
      lngRef.nativeElement.value = location.lng();
    }
  }

  addMarker(event: google.maps.MapMouseEvent) {
    if (!event.latLng) return;
    this.setMarker(event.latLng, true);
  }

  setupShadowDOMStyling() {
    // Force shadow DOM to be open so we can style it - don't try this at home kids!
    if (!this.gmpShadowPatched()) {
      const originalAttachShadow = Element.prototype.attachShadow;

      Element.prototype.attachShadow = function (init) {
        if (this.localName === 'gmp-place-autocomplete') {
          const shadow = originalAttachShadow.call(this, {
            ...init,
            mode: 'open',
          });

          const style = document.createElement('style');
          style.textContent = `
          /* Hide Google's default focus ring since we're styling the container */
          .focus-ring {
            display: none !important;
          }
        `;

          shadow.appendChild(style);
          return shadow;
        }
        return originalAttachShadow.call(this, init);
      };

      this.gmpShadowPatched.set(true);
    }
  }
}
